import { useEffect, useRef, useState } from "react";
import { Share2, MessageCircle, Link as LinkIcon, Mail, X } from "lucide-react";
import { useT } from "../lib/LangContext";

// Reusable share affordance for Lottery + Obituaries (and future
// share-worthy surfaces). Uses the platform Web Share API when the
// browser exposes `navigator.share` (mobile Safari, mobile Chrome,
// most modern Android) — that pops the OS native share sheet with
// every installed app (WhatsApp, iMessage, SMS, Signal, Email...).
//
// Desktop browsers rarely implement Web Share, so we fall back to a
// small three-option popover (WhatsApp / Copy link / Email) — the
// three channels Aruban readers actually forward content through.
//
// Props:
//   title   — Web Share API title (usually the game name / obit person)
//   text    — pre-formatted body (headline + numbers/name + date)
//   url     — absolute URL that resolves back to the specific card
//   testId  — data-testid prefix for the trigger + popover
//   compact — smaller icon variant for dense card interiors
export const ShareButton = ({ title, text, url, testId = "share", compact = false }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);

  const hasNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Close popover on outside-click / escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const stopParentLink = (e) => {
    // The obituary card wraps content in a <Link>; without this, tapping
    // the share button would navigate to the article and never open the
    // share sheet. Lottery cards aren't inside a Link but this is cheap.
    e.stopPropagation();
    e.preventDefault();
  };

  const onTrigger = async (e) => {
    stopParentLink(e);
    if (hasNativeShare) {
      try {
        await navigator.share({ title, text, url });
      } catch (_) {
        // User cancelled or share failed — do nothing (matches native UX).
      }
      return;
    }
    setOpen((v) => !v);
  };

  const shareBody = text ? `${text}\n${url}` : url;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareBody)}`;
  const mailtoHref =
    `mailto:?subject=${encodeURIComponent(title || "")}` +
    `&body=${encodeURIComponent(shareBody)}`;

  const copyLink = async (e) => {
    stopParentLink(e);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      // Very old browsers — fall through silently, user can long-press URL.
    }
  };

  const iconSize = compact ? 13 : 15;
  const btnSize = compact ? 24 : 28;

  return (
    <div ref={rootRef} className="relative inline-flex" onClick={stopParentLink}>
      <button
        type="button"
        onClick={onTrigger}
        aria-label={t("share_action")}
        title={t("share_action")}
        data-testid={`${testId}-btn`}
        className="inline-flex items-center justify-center rounded-full text-[#6B7C82] hover:text-[#0AADA0] hover:bg-[#0AADA010] transition-colors"
        style={{ width: btnSize, height: btnSize }}
      >
        <Share2 size={iconSize} strokeWidth={1.8} />
      </button>
      {open && !hasNativeShare && (
        <div
          role="menu"
          data-testid={`${testId}-menu`}
          className="absolute right-0 top-full mt-1 z-30 min-w-[172px] rounded-xl bg-white border border-[#EEEEEE] shadow-lg py-1"
          onClick={stopParentLink}
        >
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#F1F5F9]">
            <span className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold">
              {t("share_action")}
            </span>
            <button
              type="button"
              onClick={(e) => { stopParentLink(e); setOpen(false); }}
              className="text-[#9CA3AF] hover:text-[#1A2E35] rounded-full p-0.5"
              aria-label={t("share_close")}
              data-testid={`${testId}-menu-close`}
            >
              <X size={12} />
            </button>
          </div>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 text-[13px] text-[#1A2E35] hover:bg-[#F9FAFB] transition-colors"
            data-testid={`${testId}-menu-whatsapp`}
          >
            <MessageCircle size={14} className="text-[#25D366]" />
            <span>{t("share_whatsapp")}</span>
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#1A2E35] hover:bg-[#F9FAFB] transition-colors text-left"
            data-testid={`${testId}-menu-copy`}
          >
            <LinkIcon size={14} className="text-[#6B7C82]" />
            <span>{copied ? t("share_copied") : t("share_copy")}</span>
          </button>
          <a
            href={mailtoHref}
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 text-[13px] text-[#1A2E35] hover:bg-[#F9FAFB] transition-colors"
            data-testid={`${testId}-menu-email`}
          >
            <Mail size={14} className="text-[#6B7C82]" />
            <span>{t("share_email")}</span>
          </a>
        </div>
      )}
    </div>
  );
};
